import { useEffect, useRef, useState } from 'react';
import {
  getJobExpenses, scanReceipt, createExpense, updateExpense, deleteExpense,
  EXPENSE_CATEGORIES, type JobExpense, type ExpenseSummary,
} from '@/lib/api/expenses';
import { CatalogPriceReviewModal } from './CatalogPriceReviewModal';
import { InvoiceModal } from '@/components/invoices/InvoiceModal';
import { createInvoice, type CreateInvoiceData } from '@/lib/api/invoices';
import { useToast } from '@/lib/hooks/useToast';
import apiClient from '@/lib/api/client';

const CATS: Record<string, { label: string; color: string }> = {
  materials:        { label: 'Materials',        color: 'bg-amber-100 text-amber-800' },
  tools:            { label: 'Tools',            color: 'bg-blue-100 text-blue-800' },
  equipment_rental: { label: 'Equipment Rental', color: 'bg-indigo-100 text-indigo-800' },
  fuel:             { label: 'Fuel',             color: 'bg-orange-100 text-orange-800' },
  subcontractor:    { label: 'Subcontractor',    color: 'bg-purple-100 text-purple-800' },
  permits:          { label: 'Permits',          color: 'bg-teal-100 text-teal-800' },
  disposal:         { label: 'Disposal',         color: 'bg-stone-100 text-stone-800' },
  meals:            { label: 'Meals',            color: 'bg-rose-100 text-rose-800' },
  lodging:          { label: 'Lodging',          color: 'bg-pink-100 text-pink-800' },
  office:           { label: 'Office',           color: 'bg-cyan-100 text-cyan-800' },
  other:            { label: 'Other',            color: 'bg-gray-100 text-gray-700' },
};

const money = (n: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(n || 0);
const ymd = (d: string | null) => (d ? String(d).slice(0, 10) : '');
const catLabel = (c: string) => CATS[c]?.label ?? c;

interface EditDraft {
  paid?: boolean;
  due_date?: string;
  invoice_number?: string;
  vendor: string; category: string; expense_date: string;
  total: string; subtotal: string; tax: string; description: string;
}

const toDraft = (e: JobExpense): EditDraft => ({
  vendor: e.vendor ?? '',
  category: e.category,
  expense_date: ymd(e.expense_date),
  total: e.total != null ? String(e.total) : '',
  subtotal: e.subtotal != null ? String(e.subtotal) : '',
  tax: e.tax != null ? String(e.tax) : '',
  description: e.description ?? '',
  paid: e.paid !== 0 && e.paid !== false,
  due_date: e.due_date ? ymd(e.due_date) : '',
  invoice_number: e.invoice_number ?? '',
});

export function JobExpensesTab({ jobId }: { jobId: string | number }) {
  const { toast } = useToast();
  const [expenses, setExpenses] = useState<JobExpense[]>([]);
  const [summary, setSummary] = useState<ExpenseSummary>({ total: 0, count: 0, byCategory: {} });
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [priceReviewFor, setPriceReviewFor] = useState<number | null>(null);
  // Bill expenses through to a client invoice (#210)
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [billOpen, setBillOpen] = useState(false);
  const [markup, setMarkup] = useState('20');
  const [billMode, setBillMode] = useState<'per' | 'summary'>('per');
  const [invoiceSeed, setInvoiceSeed] = useState<null | {
    clientId: number; clientName: string; jobs: { id: number; title: string }[];
    items: { description: string; item_type: 'item'; quantity: number; unit: string; unit_price: number }[];
    expenseIds: number[]; billedById: Record<string, number>;
  }>(null);
  // CR #170 — attach an existing job document to a receipt
  const [docPickerFor, setDocPickerFor] = useState<number | null>(null);
  const [jobDocs, setJobDocs] = useState<{ id: number; file_name?: string; label?: string | null; is_photo?: number; uploaded_at?: string }[]>([]);
  const openDocPicker = async (expenseId: number) => {
    setDocPickerFor(expenseId);
    try {
      const r = await apiClient.get(`/jobs/${jobId}/documents`);
      setJobDocs(r.data.data ?? []);
    } catch { setJobDocs([]); }
  };
  // CR #180 — one click matches the job's receipt PDFs to expenses by
  // filename evidence (amount, date, invoice #, vendor); only sure matches apply.
  const [matching, setMatching] = useState(false);
  const autoMatch = async () => {
    setMatching(true);
    try {
      const r = await apiClient.post(`/jobs/${jobId}/expenses/auto-match`);
      const { applied, unmatched } = r.data.data;
      if (applied.length) toast.success(`Matched ${applied.length} receipt${applied.length === 1 ? '' : 's'} to expenses${unmatched.length ? ` — ${unmatched.length} still need a manual pick` : ''}.`);
      else toast.warning('No confident matches found — attach the rest manually.');
      load();
    } catch { toast.error('Auto-match failed.'); }
    finally { setMatching(false); }
  };
  const attachDoc = async (documentId: number) => {
    const expenseId = docPickerFor;
    setDocPickerFor(null);
    if (!expenseId) return;
    try {
      await apiClient.put(`/jobs/${jobId}/expenses/${expenseId}`, { document_id: documentId });
      toast.success('Receipt attached');
      load();
    } catch { toast.error('Could not attach that document.'); }
  };
  // #43 — labour cost for hours logged against this job
  const [labour, setLabour] = useState<{ cost: number; hours: number; byWorker: { name: string; hours: number; rate: number; cost: number }[] } | null>(null);
  const [labourOpen, setLabourOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      const data = await getJobExpenses(jobId);
      setExpenses(data.expenses);
      setSummary(data.summary);
    } catch {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    apiClient.get(`/jobs/${jobId}/financials`).then(r => {
      const d = r.data?.data;
      if (d) setLabour({ cost: Number(d.labourCost) || 0, hours: Number(d.labourHours) || 0, byWorker: d.labourByWorker ?? [] });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // Run one or many receipt files through the AI expense pipeline.
  const runScans = async (files: File[]) => {
    if (files.length === 0) return;
    setScanning(true);
    let ok = 0, warned = 0, failed = 0;
    try {
      for (const f of files) {
        try {
          const { warning } = await scanReceipt(jobId, f);
          if (warning) warned++; else ok++;
        } catch {
          failed++;
        }
      }
      await load();
      if (ok) toast.success(`${ok} receipt${ok === 1 ? '' : 's'} scanned & categorized`);
      if (warned) toast.warning(`${warned} saved but need details entered manually`);
      if (failed && !ok && !warned) toast.error('Could not read the receipt(s)');
      else if (failed) toast.warning(`${failed} file${failed === 1 ? '' : 's'} could not be read`);
    } finally {
      setScanning(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const onPickFiles = (files: FileList | null) => runScans(files ? Array.from(files) : []);

  const startEdit = (e: JobExpense) => { setEditingId(e.id); setDraft(toDraft(e)); };
  const startManual = () => {
    setEditingId('new');
    setDraft({ vendor: '', category: 'materials', expense_date: new Date().toISOString().slice(0, 10), total: '', subtotal: '', tax: '', description: '', paid: true, due_date: '', invoice_number: '' });
  };
  const cancelEdit = () => { setEditingId(null); setDraft(null); };

  const saveEdit = async () => {
    if (!draft) return;
    if (!draft.total || isNaN(Number(draft.total))) { toast.error('Enter a total amount'); return; }
    if (draft.paid === false && !draft.due_date) { toast.error('Unpaid invoice — set the due date'); return; }
    setSaving(true);
    const payload = {
      vendor: draft.vendor.trim() || null,
      category: draft.category,
      expense_date: draft.expense_date || null,
      total: Number(draft.total),
      subtotal: draft.subtotal ? Number(draft.subtotal) : null,
      tax: draft.tax ? Number(draft.tax) : null,
      description: draft.description.trim() || null,
      paid: draft.paid !== false,
      due_date: draft.due_date || null,
      invoice_number: (draft.invoice_number || '').trim() || null,
    };
    try {
      if (editingId === 'new') { await createExpense(jobId, payload); toast.success('Expense added'); }
      else { await updateExpense(jobId, editingId as number, payload); toast.success('Expense updated'); }
      cancelEdit();
      await load();
    } catch {
      toast.error('Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e: JobExpense) => {
    if (!window.confirm('Delete this expense and its receipt?')) return;
    try {
      await deleteExpense(jobId, e.id);
      await load();
      toast.success('Expense deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const sortedCats = Object.entries(summary.byCategory).sort((a, b) => b[1] - a[1]);

  const editForm = (isNew: boolean) => draft && (
    <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <label className="flex flex-col gap-1 col-span-2">
          <span className="text-xs text-gray-500">Vendor</span>
          <input value={draft.vendor} onChange={e => setDraft({ ...draft, vendor: e.target.value })}
            className="h-9 px-2 rounded-lg border border-gray-300 text-sm" placeholder="Supplier name" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Date</span>
          <input type="date" value={draft.expense_date} onChange={e => setDraft({ ...draft, expense_date: e.target.value })}
            className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Category</span>
          <select value={draft.category} onChange={e => setDraft({ ...draft, category: e.target.value })}
            className="h-9 px-2 rounded-lg border border-gray-300 text-sm">
            {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{catLabel(c)}</option>)}
          </select>
        </label>
      </div>
      {/* Invoice bookkeeping — required decisions at intake */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Invoice #</span>
          <input value={draft.invoice_number || ''} onChange={e => setDraft({ ...draft, invoice_number: e.target.value })}
            className="h-9 px-2 rounded-lg border border-gray-300 text-sm" placeholder="Vendor inv #" />
        </label>
        <label className="flex items-center gap-2 h-9 cursor-pointer">
          <input type="checkbox" checked={draft.paid !== false}
            onChange={e => setDraft({ ...draft, paid: e.target.checked })} className="accent-[#C77D3F]" />
          <span className="text-sm text-gray-700 font-medium">Paid</span>
        </label>
        {draft.paid === false && (
          <label className="flex flex-col gap-1">
            <span className="text-xs text-red-600 font-semibold">Due date *</span>
            <input type="date" value={draft.due_date || ''} onChange={e => setDraft({ ...draft, due_date: e.target.value })}
              className="h-9 px-2 rounded-lg border border-red-300 text-sm" />
          </label>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Subtotal</span>
          <input type="number" step="0.01" value={draft.subtotal} onChange={e => setDraft({ ...draft, subtotal: e.target.value })}
            className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Tax</span>
          <input type="number" step="0.01" value={draft.tax} onChange={e => setDraft({ ...draft, tax: e.target.value })}
            className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Total *</span>
          <input type="number" step="0.01" value={draft.total} onChange={e => setDraft({ ...draft, total: e.target.value })}
            className="h-9 px-2 rounded-lg border border-gray-300 text-sm font-semibold" />
        </label>
      </div>
      <input value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })}
        className="w-full h-9 px-2 rounded-lg border border-gray-300 text-sm" placeholder="Description (optional)" />
      <div className="flex justify-end gap-2">
        <button onClick={cancelEdit} className="h-8 px-3 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
        <button onClick={saveEdit} disabled={saving}
          className="h-8 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-60">
          {saving ? 'Saving…' : isNew ? 'Add expense' : 'Save'}
        </button>
      </div>
    </div>
  );

  const toggleSel = (id: number) =>
    setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const billable = expenses.filter(e => !e.invoiced_on_invoice_id);
  const selectedExpenses = expenses.filter(e => selected.has(e.id));
  const selTotal = selectedExpenses.reduce((s2, e) => s2 + (e.total || 0), 0);
  const billedTotal = expenses.reduce((s2, e) => s2 + (e.invoiced_on_invoice_id ? (e.billed_amount ?? e.total) : 0), 0);
  const unbilledTotal = billable.reduce((s2, e) => s2 + (e.total || 0), 0);

  // Open the normal invoice editor, pre-seeded with the chosen expenses.
  const startBilling = async () => {
    const pct = Math.max(0, parseFloat(markup) || 0);
    const mark = (n: number) => Math.round(n * (1 + pct / 100) * 100) / 100;
    try {
      const jr = await apiClient.get(`/jobs/${jobId}`);
      const job = jr.data?.data ?? jr.data;
      const clientId = job.client_id ?? job.clientId;
      if (!clientId) { alert('This job has no client, so it can\'t be invoiced yet.'); return; }
      const [cr, jl] = await Promise.all([
        apiClient.get(`/clients/${clientId}`).catch(() => null),
        apiClient.get('/jobs', { params: { clientId, limit: 100 } }).catch(() => null),
      ]);
      const cd = cr?.data?.data ?? cr?.data;
      const jd = jl?.data?.data ?? jl?.data;
      const jobsList = (Array.isArray(jd) ? jd : jd?.jobs ?? []).map((j: { id: number; title?: string }) => ({ id: j.id, title: j.title || `Job ${j.id}` }));

      const billedById: Record<string, number> = {};
      for (const e of selectedExpenses) billedById[String(e.id)] = mark(e.total || 0);

      const items = billMode === 'summary'
        ? [{
            description: `Materials & expenses${pct ? ` (incl. ${pct}% markup)` : ''}`,
            item_type: 'item' as const, quantity: 1, unit: '', unit_price: mark(selTotal),
          }]
        : selectedExpenses.map(e => ({
            description: `${catLabel(e.category)}${e.vendor ? ` — ${e.vendor}` : ''}${e.expense_date ? `, ${ymd(e.expense_date)}` : ''}${e.description ? ` (${e.description})` : ''}`,
            item_type: 'item' as const, quantity: 1, unit: '', unit_price: mark(e.total || 0),
          }));

      setInvoiceSeed({
        clientId, clientName: cd?.name || cd?.company || 'Client',
        jobs: jobsList, items,
        expenseIds: selectedExpenses.map(e => e.id), billedById,
      });
      setBillOpen(false);
    } catch {
      alert('Could not open the invoice editor — try again.');
    }
  };

  return (
    <div className="pt-4 space-y-5">
      {/* Scan / summary header */}
      <div className="rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-end gap-x-8 gap-y-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Expenses to date</p>
              <p className="text-3xl font-bold text-gray-900">{money(summary.total)}</p>
              <p className="text-xs text-gray-500 mt-0.5">{summary.count} receipt{summary.count === 1 ? '' : 's'} logged</p>
            </div>
            {labour && (
              <button onClick={() => setLabourOpen(o => !o)} className="text-left group" title="Show per-worker breakdown">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Labour to date</p>
                <p className="text-3xl font-bold text-gray-900 group-hover:text-[#C77D3F] transition-colors">
                  {money(labour.cost)}
                  <span className="material-symbols-outlined text-base text-gray-400 ml-1 align-middle">{labourOpen ? 'expand_less' : 'expand_more'}</span>
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{labour.hours}h logged by crew</p>
              </button>
            )}
            {/* #210 — how much of the job's expenses have been billed on */}
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Billed to client</p>
              <p className="text-3xl font-bold text-green-700">{money(billedTotal)}</p>
              <p className="text-xs text-gray-500 mt-0.5">{money(unbilledTotal)} not billed yet</p>
            </div>
            {labour && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Expenses + labour</p>
                <p className="text-3xl font-bold text-[#C77D3F]">{money(summary.total + labour.cost)}</p>
                <p className="text-xs text-gray-500 mt-0.5">true cost to date</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInput}
              type="file"
              accept="image/*,application/pdf,.heic,.heif"
              capture="environment"
              multiple
              className="hidden"
              onChange={e => onPickFiles(e.target.files)}
            />
            <button
              onClick={() => fileInput.current?.click()}
              disabled={scanning}
              title="Upload scanned receipts (PDF/JPG/PNG) or photograph one — you can select several at once"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-60"
            >
              <span className={`material-symbols-outlined text-lg ${scanning ? 'animate-spin' : ''}`}>
                {scanning ? 'progress_activity' : 'upload_file'}
              </span>
              {scanning ? 'Analyzing…' : 'Upload receipt'}
            </button>
            <button
              onClick={startManual}
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-white text-gray-700 border border-gray-300 text-sm font-semibold hover:bg-gray-50"
            >
              <span className="material-symbols-outlined text-lg">add</span>
              <span className="hidden sm:inline">Manual</span>
            </button>
            {/* CR #180 — attribute the job's receipt PDFs to expenses in one click */}
            <button
              onClick={autoMatch}
              disabled={matching}
              title="Match this job's unattached receipt documents to expenses by amount, date, invoice # and vendor in the filename"
              className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-white text-[#C77D3F] border border-[#C77D3F]/40 text-sm font-semibold hover:bg-[#C77D3F]/5 disabled:opacity-60"
            >
              <span className={`material-symbols-outlined text-lg ${matching ? 'animate-spin' : ''}`}>
                {matching ? 'progress_activity' : 'join_inner'}
              </span>
              <span className="hidden sm:inline">{matching ? 'Matching…' : 'Auto-match receipts'}</span>
            </button>
          </div>
        </div>

        {labourOpen && labour && labour.byWorker.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 grid sm:grid-cols-2 gap-x-8 gap-y-1">
            {labour.byWorker.map(w => (
              <div key={w.name} className="flex items-baseline justify-between text-sm">
                <span className="text-gray-700">{w.name}</span>
                <span className="text-gray-500 text-xs mx-2 flex-1 text-right">{w.hours}h @ {money(w.rate)}/h</span>
                <span className="font-semibold text-gray-900 w-20 text-right">{money(w.cost)}</span>
              </div>
            ))}
            {labour.byWorker.some(w => w.rate === 0 && w.hours > 0) && (
              <p className="text-[11px] text-amber-700 sm:col-span-2">Workers at $0/h have no hourly rate set on their employee record — their hours cost nothing here.</p>
            )}
          </div>
        )}

        {scanning && (
          <p className="mt-3 text-xs text-gray-500 flex items-center gap-1.5">
            <span className="material-symbols-outlined text-sm animate-pulse">auto_awesome</span>
            Reading vendor, date, amounts &amp; category from your receipt…
          </p>
        )}
        {!scanning && (
          <p className="mt-2 text-xs text-gray-400">
            Scan on your device and upload the file (PDF, JPG, or PNG), or snap a photo — several at once. It's read &amp; categorized automatically.
          </p>
        )}

        {sortedCats.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {sortedCats.map(([cat, amt]) => (
              <span key={cat} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${CATS[cat]?.color ?? 'bg-gray-100 text-gray-700'}`}>
                {catLabel(cat)} · {money(amt)}
              </span>
            ))}
          </div>
        )}
      </div>

      {editingId === 'new' && editForm(true)}

      {/* List */}
      {loading ? (
        <div className="h-24 animate-pulse bg-gray-100 rounded-xl" />
      ) : expenses.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          <span className="material-symbols-outlined text-4xl text-gray-300">receipt_long</span>
          <p className="text-sm mt-2">No expenses yet. Scan a receipt to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* #210 — selection action bar */}
          {billable.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-[#FAF6F0] px-3 py-2">
              <button
                onClick={() => setSelected(selected.size === billable.length ? new Set() : new Set(billable.map(e => e.id)))}
                className="text-xs font-semibold text-gray-600 hover:text-[#C77D3F]"
              >
                {selected.size === billable.length ? 'Clear selection' : `Select all unbilled (${billable.length})`}
              </button>
              <span className="text-xs text-gray-500">
                {selected.size > 0 ? `${selected.size} selected · ${money(selTotal)}` : 'Tick expenses to bill them to the client'}
              </span>
              <button
                onClick={() => setBillOpen(true)}
                disabled={selected.size === 0}
                className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-40"
              >
                <span className="material-symbols-outlined text-base">receipt_long</span>
                Add to invoice
              </button>
            </div>
          )}
          {expenses.map(e => (
            <div key={e.id} className={`rounded-xl border bg-white p-3 ${selected.has(e.id) ? 'border-[#C77D3F] ring-1 ring-[#C77D3F]/30' : 'border-gray-200'}`}>
              {editingId === e.id ? editForm(false) : (
                <div className="flex items-center gap-3">
                  {/* #210 — pick expenses to bill to the client */}
                  <input
                    type="checkbox"
                    checked={selected.has(e.id)}
                    disabled={!!e.invoiced_on_invoice_id}
                    onChange={() => toggleSel(e.id)}
                    title={e.invoiced_on_invoice_id ? 'Already billed to a client invoice' : 'Select to add to an invoice'}
                    className="shrink-0 w-5 h-5 accent-[#C77D3F] disabled:opacity-30"
                  />
                  {e.receipt_url ? (
                    <a href={e.receipt_url} target="_blank" rel="noopener noreferrer" title="View receipt"
                      className="shrink-0 w-12 h-12 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center">
                      {/\.(png|jpe?g|webp|gif|heic|heif)$/i.test(e.receipt_url)
                        ? <img src={e.receipt_url} alt="receipt" className="w-full h-full object-cover" />
                        : <span className="material-symbols-outlined text-gray-400">description</span>}
                    </a>
                  ) : (
                    /* CR #170 — no receipt attached yet: pick an EXISTING job
                       document instead of forcing a fresh upload */
                    <button
                      type="button"
                      onClick={() => openDocPicker(e.id)}
                      title="Attach an existing job document as this receipt"
                      className="shrink-0 w-12 h-12 rounded-lg border border-dashed border-[#C77D3F]/50 bg-[#FAF6F0] flex items-center justify-center text-[#C77D3F] hover:bg-[#C77D3F]/10">
                      <span className="material-symbols-outlined">attach_file</span>
                    </button>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900 truncate">{e.vendor || 'Unknown vendor'}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CATS[e.category]?.color ?? 'bg-gray-100 text-gray-700'}`}>
                        {catLabel(e.category)}
                      </span>
                      {e.ai_analyzed && (
                        <span className="inline-flex items-center gap-0.5 text-xs text-primary" title="Auto-extracted by AI">
                          <span className="material-symbols-outlined text-sm">auto_awesome</span>
                        </span>
                      )}
                      {(e.paid === 0 || e.paid === false) && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                          UNPAID{e.due_date ? ` · due ${ymd(e.due_date)}` : ''}
                        </span>
                      )}
                      {e.invoice_number && <span className="text-[10px] text-gray-400">inv {e.invoice_number}</span>}
                      {e.invoiced_on_invoice_id && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800"
                          title={e.billed_amount != null ? `Billed ${money(e.billed_amount)} to the client` : 'Billed to the client'}>
                          BILLED{e.billed_invoice_number ? ` · ${e.billed_invoice_number}` : ''}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      {ymd(e.expense_date) || 'No date'}{e.description ? ` · ${e.description}` : ''}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold text-gray-900">{money(e.total)}</p>
                    {e.tax != null && <p className="text-xs text-gray-400">tax {money(e.tax)}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {e.category === 'materials' && e.ai_analyzed && (
                      <button onClick={() => setPriceReviewFor(e.id)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-primary" title="Update catalog pricing from this receipt">
                        <span className="material-symbols-outlined text-lg">price_check</span>
                      </button>
                    )}
                    <button onClick={() => startEdit(e)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-primary" title="Edit">
                      <span className="material-symbols-outlined text-lg">edit</span>
                    </button>
                    <button onClick={() => handleDelete(e)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600" title="Delete">
                      <span className="material-symbols-outlined text-lg">delete</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* #210 — markup + grouping, then the normal invoice editor */}
      {billOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setBillOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={ev => ev.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">Add {selected.size} expense{selected.size === 1 ? '' : 's'} to an invoice</h3>
              <p className="text-xs text-gray-500 mt-0.5">Cost {money(selTotal)} — the invoice opens next so you can review before saving.</p>
            </div>
            <div className="p-4 space-y-4">
              <label className="block">
                <span className="text-xs font-medium text-gray-500">Markup</span>
                <div className="flex items-center gap-2 mt-1">
                  <input inputMode="decimal" value={markup} onChange={ev => setMarkup(ev.target.value)}
                    className="h-10 w-24 px-3 rounded-lg border border-gray-300 text-sm" />
                  <span className="text-sm text-gray-500">%</span>
                  <span className="ml-auto text-sm font-bold text-gray-900">
                    bills {money(selTotal * (1 + (parseFloat(markup) || 0) / 100))}
                  </span>
                </div>
              </label>
              <div>
                <span className="text-xs font-medium text-gray-500">Show on the invoice as</span>
                <div className="flex gap-2 mt-1">
                  {([['per', 'One line each'], ['summary', 'One summary line']] as const).map(([m, label]) => (
                    <button key={m} onClick={() => setBillMode(m)}
                      className={`h-9 px-3 rounded-lg text-xs font-semibold border ${billMode === m ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-100">
              <button onClick={() => setBillOpen(false)} className="h-10 px-4 rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-100">Cancel</button>
              <button onClick={startBilling} className="h-10 px-4 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90">Continue</button>
            </div>
          </div>
        </div>
      )}

      {invoiceSeed && (
        <InvoiceModal
          isOpen
          onClose={() => setInvoiceSeed(null)}
          clientId={invoiceSeed.clientId}
          clientName={invoiceSeed.clientName}
          jobs={invoiceSeed.jobs}
          defaultJobId={Number(jobId)}
          seedItems={invoiceSeed.items}
          onSave={async (data) => {
            // expense_ids/expense_billed are reconciled server-side in the same
            // transaction that writes the invoice (#210)
            const res = await createInvoice({
              ...(data as CreateInvoiceData),
              client_id: invoiceSeed.clientId,
              job_id: Number(jobId),
              expense_ids: invoiceSeed.expenseIds,
              expense_billed: invoiceSeed.billedById,
            } as CreateInvoiceData);
            toast.success(`Invoice ${res.invoiceNumber} created with ${invoiceSeed.expenseIds.length} expense line${invoiceSeed.expenseIds.length === 1 ? '' : 's'}`);
            setInvoiceSeed(null);
            setSelected(new Set());
            load();
          }}
        />
      )}

      {priceReviewFor != null && (
        <CatalogPriceReviewModal
          jobId={jobId}
          expenseId={priceReviewFor}
          onClose={() => setPriceReviewFor(null)}
          onApplied={() => { /* catalog changed, not the expense list */ }}
        />
      )}

      {/* CR #170 — attach an EXISTING job document to a receipt */}
      {docPickerFor !== null && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDocPickerFor(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[70vh] flex flex-col" onClick={ev => ev.stopPropagation()}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
              <span className="material-symbols-outlined text-[#C77D3F]">attach_file</span>
              <p className="font-bold text-gray-900 flex-1">Attach a job document as the receipt</p>
              <button onClick={() => setDocPickerFor(null)} className="text-gray-400 hover:text-gray-700">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="overflow-y-auto divide-y divide-gray-50">
              {jobDocs.length === 0 && (
                <p className="text-sm text-gray-400 p-6 text-center">No documents on this job yet — upload one in Docs &amp; Photos first.</p>
              )}
              {jobDocs.map(d => (
                <button key={d.id} onClick={() => attachDoc(d.id)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50">
                  <span className="material-symbols-outlined text-gray-400">
                    {d.is_photo ? 'image' : 'description'}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-gray-900 truncate">{d.label || d.file_name}</span>
                    {d.uploaded_at && <span className="block text-xs text-gray-400">{String(d.uploaded_at).slice(0, 10)}</span>}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
